import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  LayoutDashboard, Users, UserCircle, Target, Handshake, FileText, ShoppingCart,
  FolderKanban, MapPin, CheckSquare, Ticket, Package, Warehouse, Receipt, 
  CreditCard, BookOpen, FileSpreadsheet, UserCog, Building2, CalendarClock, 
  Wallet, Calendar, FileArchive, Coins, Terminal, Settings, LogOut, Menu, X,
  ChevronDown, ChevronRight, Truck, Building, Shield, Globe, FileDown, Phone
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../components/ui/dropdown-menu';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';

// Module to permission mapping
const MODULE_PERMISSIONS = {
  'Dashboard': 'dashboard',
  'CRM': 'crm',
  'Sales': 'sales',
  'Projects': 'projects',
  'Procurement': 'procurement',
  'Inventory': 'inventory',
  'Finance': 'finance',
  'HR': 'hr',
  'POS Terminal': 'pos',
  'Documents': 'documents',
  'Currencies': 'settings',
  'Settings': 'settings',
  'Reports': 'reports',
};

const menuItems = [
  { name: 'Dashboard', icon: LayoutDashboard, path: '/dashboard', module: 'dashboard' },
  {
    name: 'CRM',
    icon: Users,
    module: 'crm',
    children: [
      { name: 'Clients', icon: Users, path: '/crm/clients' },
      { name: 'Contacts', icon: UserCircle, path: '/crm/contacts' },
      { name: 'Leads', icon: Target, path: '/crm/leads' },
      { name: 'Deals', icon: Handshake, path: '/crm/deals' },
    ],
  },
  {
    name: 'Sales',
    icon: FileText,
    module: 'sales',
    children: [
      { name: 'Estimates', icon: FileText, path: '/sales/estimates' },
      { name: 'Quotations', icon: FileText, path: '/sales/quotations' },
      { name: 'Sales Orders', icon: ShoppingCart, path: '/sales/orders' },
      { name: 'Delivery Challan', icon: Truck, path: '/sales/delivery-challan' },
      { name: 'Payment Receipts', icon: CreditCard, path: '/sales/receipts' },
      { name: 'Credit Notes', icon: Receipt, path: '/sales/credit-notes' },
{ name: "Maya Leads", icon: Phone, path: "/sales/maya-leads" },
    ],
  },
  {
    name: 'Projects',
    icon: FolderKanban,
    module: 'projects',
    children: [
      { name: 'Projects', icon: FolderKanban, path: '/projects/list' },
      { name: 'Sites', icon: MapPin, path: '/projects/sites' },
      { name: 'Tasks', icon: CheckSquare, path: '/projects/tasks' },
      { name: 'Tickets', icon: Ticket, path: '/projects/tickets' },
    ],
  },
  {
    name: 'Procurement',
    icon: Truck,
    module: 'procurement',
    children: [
      { name: 'Suppliers', icon: Truck, path: '/procurement/suppliers' },
      { name: 'Products', icon: Package, path: '/procurement/products' },
      { name: 'Purchase Orders', icon: FileText, path: '/procurement/orders' },
      { name: 'Bills', icon: Receipt, path: '/procurement/bills' },
      { name: 'Payments', icon: CreditCard, path: '/procurement/payments' },
    ],
  },
  {
    name: 'Inventory',
    icon: Warehouse,
    module: 'inventory',
    children: [
      { name: 'Warehouses', icon: Warehouse, path: '/inventory/warehouses' },
      { name: 'Stock', icon: Package, path: '/inventory/stock' },
    ],
  },
  {
    name: 'Finance',
    icon: Wallet,
    module: 'finance',
    children: [
      { name: 'Invoices', icon: Receipt, path: '/finance/invoices' },
      { name: 'Expenses', icon: CreditCard, path: '/finance/expenses' },
      { name: 'Chart of Accounts', icon: BookOpen, path: '/finance/accounts' },
      { name: 'Journal Entries', icon: FileSpreadsheet, path: '/finance/journal' },
    ],
  },
  {
    name: 'HR',
    icon: UserCog,
    module: 'hr',
    children: [
      { name: 'Employees', icon: Users, path: '/hr/employees' },
      { name: 'Departments', icon: Building2, path: '/hr/departments' },
      { name: 'Attendance', icon: CalendarClock, path: '/hr/attendance' },
      { name: 'Payroll', icon: Wallet, path: '/hr/payroll' },
      { name: 'Leave', icon: Calendar, path: '/hr/leave' },
    ],
  },
  { name: 'POS Terminal', icon: Terminal, path: '/pos', module: 'pos' },
  { name: 'Documents', icon: FileArchive, path: '/documents', module: 'documents' },
  { name: 'Currencies', icon: Coins, path: '/currencies', module: 'settings' },
  { name: 'Reports', icon: FileDown, path: '/reports', module: 'reports' },
  { name: 'Settings', icon: Settings, path: '/settings', module: 'settings' },
];

// Admin-only menu items
const adminMenuItems = [
  {
    name: 'Administration',
    icon: Shield,
    adminOnly: true,
    children: [
      { name: 'Master Dashboard', icon: Building2, path: '/admin/master-dashboard' },
      { name: 'Companies', icon: Building, path: '/admin/companies' },
      { name: 'Users', icon: Users, path: '/admin/users' },
      { name: 'Roles', icon: Shield, path: '/admin/roles' },
    ],
  },
];

// Check if user has permission to access a module
const hasModulePermission = (userRole, module, permissions) => {
  // Super admin has access to everything
  if (userRole === 'Super Admin' || userRole === 'admin') return true;
  
  // If no permissions defined, allow access (for backward compatibility)
  if (!permissions || permissions.length === 0) return true;
  
  // Check if user has permission for this module
  const modulePermission = permissions.find(p => p.module === module);
  if (!modulePermission) return false;
  
  // Check if user has at least read permission
  return modulePermission.read === true;
};

const MenuItem = ({ item, isActive, onClick, userRole, userPermissions }) => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { isSuperAdmin } = useAuth();
  // Round 10: a deliberate close of the section holding the current page must
  // stick until navigation. Without this, open below is forced true whenever
  // a child page is active, so the section can never be closed by its header.
  const [closedPath, setClosedPath] = useState(null);
  useEffect(() => { setClosedPath(null); }, [location.pathname]);

  // Hide admin-only items for non-admins
  if (item.adminOnly && !isSuperAdmin) {
    return null;
  }

  // Check module permission (skip for items without module defined)
  if (item.module && !hasModulePermission(userRole, item.module, userPermissions)) {
    return null;
  }

  if (item.children) {
    const isChildActive = item.children.some(child => location.pathname.startsWith(child.path));
    const effectiveOpen = isOpen || (isChildActive && closedPath !== location.pathname);
    const handleOpenChange = (open) => {
      setIsOpen(open);
      setClosedPath(open ? null : location.pathname);
    };
    
    return (
      <Collapsible open={effectiveOpen} onOpenChange={handleOpenChange}>
        <CollapsibleTrigger asChild>
          <button
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all ${
              isChildActive 
                ? 'bg-teal-50 text-teal-700' 
                : 'text-slate-600 hover:text-teal-700 hover:bg-teal-50/50'
            }`}
            data-testid={`menu-${item.name.toLowerCase().replace(/\s/g, '-')}`}
          >
            <item.icon className="h-5 w-5" strokeWidth={1.5} />
            <span className="flex-1 text-left">{item.name}</span>
            {effectiveOpen ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="ml-4 pl-4 border-l border-slate-200 mt-1 space-y-1">
            {item.children.map((child) => (
              <Link
                key={child.path}
                to={child.path}
                className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-all ${
                  location.pathname === child.path
                    ? 'bg-teal-100 text-teal-700 font-medium'
                    : 'text-slate-500 hover:text-teal-700 hover:bg-teal-50/50'
                }`}
                data-testid={`menu-${child.name.toLowerCase().replace(/\s/g, '-')}`}
              >
                <child.icon className="h-4 w-4" strokeWidth={1.5} />
                <span>{child.name}</span>
              </Link>
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>
    );
  }

  return (
    <Link
      to={item.path}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all ${
        isActive
          ? 'bg-teal-50 text-teal-700'
          : 'text-slate-600 hover:text-teal-700 hover:bg-teal-50/50'
      }`}
      onClick={onClick}
      data-testid={`menu-${item.name.toLowerCase().replace(/\s/g, '-')}`}
    >
      <item.icon className="h-5 w-5" strokeWidth={1.5} />
      <span>{item.name}</span>
    </Link>
  );
};

const Layout = ({ children }) => {
  const { user, logout, currentCompany, switchCompany, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const companies = user?.companies || [];
  
  // Get user's current role and permissions
  const userRole = user?.role || (isSuperAdmin ? 'Super Admin' : 'Viewer');
  const userPermissions = user?.permissions || [];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-screen w-64 bg-white border-r border-slate-200 transform transition-transform lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between h-16 px-4 border-b border-slate-200">
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="brand-logo">
            <img src="/merai-icon.png" alt="Merai" className="w-8 h-8 rounded-lg" />
            <span className="font-heading font-bold text-lg text-slate-900">merai</span>
          </Link>
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        <ScrollArea className="h-[calc(100vh-4rem)] py-4 px-3">
          <nav className="space-y-1">
            {menuItems.map((item) => (
              <MenuItem
                key={item.name}
                item={item}
                isActive={location.pathname === item.path}
                onClick={() => setSidebarOpen(false)}
                userRole={userRole}
                userPermissions={userPermissions}
              />
            ))}
            
            {/* Admin Menu */}
            {isSuperAdmin && (
              <>
                <div className="pt-4 pb-2">
                  <p className="px-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Administration
                  </p>
                </div>
                {adminMenuItems.map((item) => (
                  <MenuItem
                    key={item.name}
                    item={item}
                    isActive={location.pathname === item.path}
                    onClick={() => setSidebarOpen(false)}
                    userRole={userRole}
                    userPermissions={userPermissions}
                  />
                ))}
              </>
            )}
          </nav>
        </ScrollArea>
      </aside>

      {/* Main content */}
      <div className="lg:ml-64">
        {/* Header */}
        <header className="sticky top-0 z-30 h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-toggle"
            >
              <Menu className="h-5 w-5" />
            </Button>
            
            {/* Company Switcher */}
            {companies.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="flex items-center gap-2 h-9" data-testid="company-switcher">
                    {currentCompany?.logo_url ? (
              <img src={currentCompany.logo_url} alt="Company logo" className="h-6 w-6 object-contain" />
            ) : (
              <Globe className="h-4 w-4 text-teal-600" />
            )}
                    <span className="font-medium text-sm">
                      {currentCompany?.name || currentCompany?.code || 'Select Company'}
                    </span>
                    {currentCompany?.code && (
                      <Badge variant="secondary" className="ml-1 text-xs">{currentCompany.code}</Badge>
                    )}
                    <ChevronDown className="h-3 w-3 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-64">
                  <DropdownMenuLabel>Switch Company</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {companies.map((c) => (
                    <DropdownMenuItem
                      key={c.id}
                      onClick={() => switchCompany({ id: c.id, name: c.name, code: c.code })}
                      className={currentCompany?.id === c.id ? 'bg-teal-50' : ''}
                    >
                      <Building className="h-4 w-4 mr-2" />
                      <span className="flex-1">{c.name}</span>
                      <Badge variant="outline" className="text-xs">{c.code}</Badge>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          <div className="flex items-center gap-4">
            {isSuperAdmin && (
              <Badge className="bg-purple-100 text-purple-700 font-medium">
                <Shield className="h-3 w-3 mr-1" />
                Super Admin
              </Badge>
            )}
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-teal-100 text-teal-700 text-sm font-medium">
                      {user ? getInitials(user.name) : 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:block text-sm font-medium text-slate-700">
                    {user?.name || 'User'}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem className="text-slate-600">
                  <UserCircle className="h-4 w-4 mr-2" />
                  {user?.email}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
